import { useMemo, useState } from 'react';
import { Button, Center, Group, Loader, Stack, Switch, TextInput, Title } from '@mantine/core';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { useOrganizations } from '@/api/orgs';
import { useProjects } from '@/api/projects';
import { EmptyState } from '@/components/EmptyState';
import { usePageTitle } from '@/components/usePageTitle';
import { CreateProjectModal } from '@/features/projects/CreateProjectModal';
import { ProjectsTable } from '@/features/staff/ProjectsTable';

export const Route = createFileRoute('/_staff/staff/projects/')({ component: StaffProjectsPage });

function StaffProjectsPage() {
  const { t } = useTranslation('projects');
  usePageTitle(t('staff.projects'));
  const navigate = useNavigate();
  const [archived, setArchived] = useState(false);
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const { data, isPending, isError, error } = useProjects({ includeArchived: archived });
  const { data: orgs } = useOrganizations();

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle || !data) return data ?? [];
    return data.filter((p) => [p.name, p.organization.name].some((s) => s.toLowerCase().includes(needle)));
  }, [data, q]);

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={1}>{t('staff.projects')}</Title>
        <Button onClick={() => setCreateOpen(true)}>{t('staff.newProject')}</Button>
      </Group>
      <Group>
        <TextInput placeholder={t('staff.search')} value={q} onChange={(e) => setQ(e.currentTarget.value)} style={{ flex: 1, maxWidth: 480 }} />
        <Switch label={t('list.showArchived')} checked={archived} onChange={(e) => setArchived(e.currentTarget.checked)} />
      </Group>
      {isPending ? (
        <Center py="xl">
          <Loader color="forest" />
        </Center>
      ) : isError ? (
        <EmptyState title={errorMessage(error)} />
      ) : filtered.length === 0 ? (
        <EmptyState title={t('list.empty')} />
      ) : (
        <ProjectsTable projects={filtered} />
      )}
      {orgs && (
        <CreateProjectModal
          opened={createOpen}
          onClose={() => setCreateOpen(false)}
          organizations={orgs}
          showInternal
          navigateTo={(id) => void navigate({ to: '/staff/projects/$projectId', params: { projectId: id } })}
        />
      )}
    </Stack>
  );
}
